import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike } from '@krp/client-runtime'

import type { AboutClientInfo, AboutReport, AboutRowVm, AboutService, AboutState, AboutVm } from './types'

export interface AboutVmInput {
  query: QueryResultLike<AboutReport>
  /** Сборка самого клиента; `undefined` — строки клиента нет (тесты без сборки). */
  client?: AboutClientInfo | undefined
  /** Часовой пояс показа (IANA); не задан — пояс человека. Нужен тестам. */
  timeZone?: string | undefined
}

const NO_ROWS: readonly AboutRowVm[] = []
const NOT_REPORTED = 'не сообщается службой'

const STATE_TEXT: Record<AboutState, string> = {
  ok: 'На связи',
  lagging: 'Отстаёт от релиза',
  down: 'Нет связи',
  'not-configured': 'Не подключена',
}

/** Момент ISO → «24.10.2026, 14:32» (пояс человека или заданный); не разобралось — как пришло. */
export function formatMoment(value: string | null, timeZone: string | undefined): string | null {
  if (value === null) return null
  const moment = new Date(value)
  if (Number.isNaN(moment.getTime())) return value
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone !== undefined ? { timeZone } : {}),
  }).format(moment)
}

function serviceRow(service: AboutService, timeZone: string | undefined): AboutRowVm {
  const reachable = service.state === 'ok' || service.state === 'lagging'
  const versionText = service.version ?? (reachable ? (service.versionReported ? '—' : NOT_REPORTED) : '—')
  return {
    id: service.id,
    name: service.name,
    state: service.state,
    stateText: STATE_TEXT[service.state],
    healthy: service.state === 'ok',
    versionText,
    commitText: service.commit ?? (reachable ? NOT_REPORTED : '—'),
    builtText: formatMoment(service.builtAt, timeZone) ?? (reachable ? NOT_REPORTED : '—'),
    startedText: formatMoment(service.startedAt, timeZone) ?? (reachable ? NOT_REPORTED : '—'),
    checkedText: formatMoment(service.checkedAt, timeZone) ?? '—',
    note: service.message,
  }
}

function clientRow(client: AboutClientInfo, timeZone: string | undefined): AboutRowVm {
  return {
    id: 'web-client',
    name: 'Клиент (веб)',
    state: 'client',
    stateText: client.serverBuildDiffers ? 'На сервере другая версия' : 'Актуален',
    healthy: !client.serverBuildDiffers,
    versionText: client.commit ?? 'локальная сборка',
    commitText: client.commit ?? '—',
    builtText: formatMoment(client.builtAt, timeZone) ?? 'не сообщается',
    startedText: 'не сообщается службой',
    checkedText: 'сейчас',
    note: client.serverBuildDiffers ? 'Обновите страницу' : null,
  }
}

function clientNoticeOf(client: AboutClientInfo | undefined): string | null {
  if (client === undefined || !client.serverBuildDiffers || client.serverBuild === null) return null
  return `Вы смотрите версию клиента ${client.commit ?? 'локальной сборки'}, на сервере — ${client.serverBuild}. Обновите страницу.`
}

function copyTextOf(report: AboutReport, rows: readonly AboutRowVm[], generatedText: string): string {
  const lines = rows.map(
    (row) =>
      `${row.name}: версия ${row.versionText}; коммит ${row.commitText}; собрана ${row.builtText}; выкачана ${row.startedText}; ` +
      `${row.stateText}${row.note === null ? '' : ` (${row.note})`}; проверено ${row.checkedText}`,
  )
  const release = report.release === null ? 'не назван' : report.release
  return [`Версии служб Kalmykov Retail Platform. Список собран ${generatedText}. Релиз шлюза: ${release}.`, ...lines].join('\n')
}

/** Фабрика: один экземпляр на смонтированный binding (D07). */
export const makeSelectAboutVm = () =>
  createSelector(
    [
      (input: AboutVmInput) => input.query.isUninitialized,
      (input: AboutVmInput) => input.query.isLoading,
      (input: AboutVmInput) => input.query.isError,
      (input: AboutVmInput) => input.query.isSuccess,
      (input: AboutVmInput) => input.query.error,
      (input: AboutVmInput) => input.query.data,
      (input: AboutVmInput) => input.client,
      (input: AboutVmInput) => input.timeZone,
    ],
    (isUninitialized, isLoading, isError, isSuccess, error, data, client, timeZone): AboutVm => {
      const query = queryView(
        {
          isUninitialized,
          isLoading,
          isError,
          isSuccess,
          ...(error !== undefined ? { error } : {}),
          ...(data !== undefined ? { data } : {}),
        },
        (report) => report.services.length === 0,
      )
      // Ошибка при старых данных — ошибка, а не старые версии как текущие (F06).
      const report = query.status === 'ready' ? data : undefined
      if (report === undefined) {
        return { query, rows: NO_ROWS, summary: null, clientNotice: null, generatedText: '', copyText: '' }
      }
      const rows = [
        ...(client === undefined ? [] : [clientRow(client, timeZone)]),
        ...report.services.map((service) => serviceRow(service, timeZone)),
      ]
      const attention = rows.filter((row) => !row.healthy).length
      const generatedText = formatMoment(report.generatedAt, timeZone) ?? ''
      return {
        query,
        rows,
        summary: attention === 0 ? null : `Требуют внимания: ${attention} из ${rows.length}`,
        clientNotice: clientNoticeOf(client),
        generatedText,
        copyText: copyTextOf(report, rows, generatedText),
      }
    },
  )
